import { CRMUser } from "@swivl-mono/types";
import { DataTable, DataTableVariant, Modal, SearchInput } from "@swivl-mono/ui";
import { ColumnDef } from "@tanstack/react-table";
import React from "react";

import { SelectedCustomerCard } from "./SelectedCustomerCard";
import { useCustomerPicker } from "./useCustomerPicker";

export interface SelectedCustomer {
  id: number;
  name: string;
  phone?: string | null;
  locationName?: string | null;
}

interface CustomerPickerModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (customer: SelectedCustomer) => void;
  selectedCustomer?: SelectedCustomer | null;
  onRemove?: () => void;
}

const columns: ColumnDef<CRMUser, any>[] = [
  { header: "First Name", accessorKey: "firstName", cell: (info) => info.getValue() ?? "", enableSorting: false },
  { header: "Last Name", accessorKey: "lastName", cell: (info) => info.getValue() ?? "", enableSorting: false },
  {
    header: "Phone",
    accessorKey: "phoneNumbers",
    cell: (info) => {
      const phones = info.getValue() as string[];
      return phones?.[0] ?? "";
    },
    enableSorting: false
  },
  {
    header: "Email",
    accessorKey: "emails",
    cell: (info) => {
      const emails = info.getValue() as string[];
      return emails?.[0] ?? "";
    },
    enableSorting: false
  },
  {
    header: "Location",
    accessorKey: "locations",
    cell: (info) => {
      const locations = info.getValue() as CRMUser["locations"];
      return locations?.[0]?.name ?? "";
    },
    enableSorting: false
  }
];

export function CustomerPickerModal({ open, onClose, onSelect, selectedCustomer, onRemove }: CustomerPickerModalProps): React.ReactNode {
  const {
    search,
    setSearch,
    users,
    loading,
    resolvedCustomer,
    selectedUserSubtitle,
    hasNext,
    hasPrev,
    handleNext,
    handlePrev,
    handleRemove,
    handleSelectUser,
    userMap
  } = useCustomerPicker({ open, onClose, onSelect, selectedCustomer, onRemove });

  const handleSelectCell = (selectedId?: number): void => {
    if (selectedId == null) return;
    const user = userMap.get(selectedId);
    if (user) handleSelectUser(user);
  };

  return (
    <Modal isOpen={open} onClose={(isOpen) => !isOpen && onClose()} title="Select Customer" className="flex h-[640px] w-[800px] flex-col">
      <div className="flex min-h-0 flex-1 flex-col px-4 pb-4">
        {/* Search */}
        <div className="shrink-0 pb-4">
          <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search" autoFocus />
        </div>

        {/* Selected customer card */}
        {resolvedCustomer && onRemove && (
          <div className="shrink-0 pb-4">
            <SelectedCustomerCard name={resolvedCustomer.name} subtitle={selectedUserSubtitle} onRemove={handleRemove} />
          </div>
        )}

        {/* Table */}
        <div className="min-h-0 flex-1">
          <DataTable
            columns={columns}
            data={users}
            loading={loading}
            areRowsSelectable
            handleSelectCell={handleSelectCell}
            hasFooter
            pinFooter
            variant={DataTableVariant.SECONDARY}
            manualConfig={{ manualPagination: true, useCursorPagination: true }}
            paginationState={{
              hasNext,
              hasPrev,
              onNext: handleNext,
              onPrev: handlePrev
            }}
          />
        </div>
      </div>
    </Modal>
  );
}
